/**
 * S-13 화면용 순수 유틸 — 상태 변환·검증·정규화 로직 집약.
 * 계약(s13-types.ts) 규칙을 화면에서 재현하되, 편집 허용 범위는
 * "방문자명·방문일만" 이라는 제약을 코드로 강제한다.
 */
import {
  S13Payload,
  S13Sheet,
  S13AreaRow,
  S13Visit,
  VISIT_DATE_PATTERN,
  ISO_DATE_PATTERN,
} from './s13-types';

/** 방문 슬롯 최대 개수 (C/E/G/I열 → 4개) */
export const MAX_VISIT_SLOTS = 4;

/** 시트 번호 → 표시 라벨 */
export function sheetLabel(sheetNo: number): string {
  return `${sheetNo}번구역`;
}

/**
 * 마지막 방문일(B열 대응) 파생 — visits의 최대 date.
 * date 문자열은 'YY-MM-DD' 형식이라 사전순 정렬이 곧 시간순이다
 * (같은 세기 내 2자리 연도 가정, 원본 규칙과 동일).
 * 유효한 방문일이 하나도 없으면 빈 문자열.
 */
export function deriveLastVisitDate(visits: S13Visit[]): string {
  const dates = visits
    .map((v) => v.date)
    .filter((d) => VISIT_DATE_PATTERN.test(d));
  if (dates.length === 0) return '';
  return dates.reduce((max, d) => (d > max ? d : max), dates[0]);
}

/** 방문일 형식 검증 — 빈 문자열은 "미입력"으로 보고 유효 취급(에러 아님) */
export function isValidVisitDate(date: string): boolean {
  if (date.trim() === '') return true;
  return VISIT_DATE_PATTERN.test(date);
}

/**
 * 단일 방문 슬롯이 "내용 있음"인지 — 이름 또는 날짜 중 하나라도 채워졌는가.
 * 정규화(빈 슬롯 제거) 판단 기준.
 */
export function isVisitFilled(v: S13Visit): boolean {
  return v.name.trim() !== '' || v.date.trim() !== '';
}

/**
 * 슬롯 정규화 — 내용 있는 방문만 앞에서부터 채우고 최대 4개로 자른다.
 * 중간 빈 슬롯 제거 후 저장/내보내기 전에 호출.
 */
export function normalizeVisits(visits: S13Visit[]): S13Visit[] {
  return visits
    .filter(isVisitFilled)
    .slice(0, MAX_VISIT_SLOTS)
    .map((v) => ({ name: v.name.trim(), date: v.date.trim() }));
}

/** payload 전체 정규화 (모든 행의 visits 정규화) */
export function normalizePayload(payload: S13Payload): S13Payload {
  return {
    ...payload,
    sheets: payload.sheets.map((sheet) => ({
      ...sheet,
      rows: sheet.rows.map((row) => ({
        ...row,
        visits: normalizeVisits(row.visits),
      })),
    })),
  };
}

/**
 * 편집 표시용으로 각 행의 방문 슬롯을 항상 4개로 패딩한다
 * (빈 슬롯 입력을 위해). 저장 시 normalize로 되돌린다.
 */
export function padVisits(visits: S13Visit[]): S13Visit[] {
  const padded = visits.slice(0, MAX_VISIT_SLOTS).map((v) => ({ ...v }));
  while (padded.length < MAX_VISIT_SLOTS) {
    padded.push({ name: '', date: '' });
  }
  return padded;
}

/** payload deep clone (편집 초기 상태 구성용) */
export function clonePayload(payload: S13Payload): S13Payload {
  return {
    ...payload,
    sheets: payload.sheets.map((sheet) => ({
      ...sheet,
      rows: sheet.rows.map((row) => ({
        ...row,
        visits: row.visits.map((v) => ({ ...v })),
      })),
    })),
  };
}

/**
 * 전체 payload에 형식 오류인 방문일이 하나라도 있는지.
 * true면 저장/내보내기 차단.
 */
export function hasInvalidDate(payload: S13Payload): boolean {
  return payload.sheets.some((sheet) =>
    sheet.rows.some((row) =>
      row.visits.some((v) => !isValidVisitDate(v.date)),
    ),
  );
}

/**
 * 두 payload가 (편집 관점에서) 동일한지 비교 — 정규화 후 JSON 비교.
 * "미저장 변경" 판단용. baseDate/month/sheet 구조는 편집 불가라
 * visits만 실질적으로 달라진다.
 */
export function payloadsEqual(a: S13Payload, b: S13Payload): boolean {
  return (
    JSON.stringify(normalizePayload(a)) === JSON.stringify(normalizePayload(b))
  );
}

/** 'YYYY-MM-DD' 형식 검증 (start/end) — 빈 문자열은 무효 */
export function isValidIsoDate(date: string): boolean {
  return ISO_DATE_PATTERN.test(date);
}

/**
 * 조회 기간 검증 — 둘 다 유효한 ISO 날짜이고 start <= end 인지.
 * 반환: null(정상) 또는 오류 메시지.
 * 'YYYY-MM-DD'는 사전순 비교가 곧 시간순이라 Date 파싱 없이 문자열 비교.
 */
export function validatePeriod(start: string, end: string): string | null {
  if (!isValidIsoDate(start) || !isValidIsoDate(end)) {
    return '시작일과 종료일을 모두 올바르게 선택하세요';
  }
  if (start > end) {
    return '시작일이 종료일보다 늦을 수 없습니다';
  }
  return null;
}

export type { S13Payload, S13Sheet, S13AreaRow, S13Visit };
